import React, { useState } from "react"
import { Upload, message, Progress, Flex, Col } from "antd"
import PropTypes from "prop-types"
import * as XLSX from "xlsx";
import { DeleteOutlined } from "@ant-design/icons"
import { Icon } from "ui/Icon"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import { useTranslation } from "react-i18next"
import styles from "./UploadFile.module.css"
import { File } from "ui/Icon/DSIcon/File"

const { Dragger } = Upload

const UploadFile = ({ onFileListChange, handleFileChangeImport, setFileData }) => {
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadComplete, setUploadComplete] = useState(false)
  const { t } = useTranslation()


  const beforeUpload = (file) => {
    const isXlsx = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isXlsx) {
      message.error("Only .xlsx format is supported!");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet);

      if (rows.length === 0) {
        message.error("Uploaded file is empty.");
        return;
      }

      if (rows.length > 500) {
        message.error("The file exceeds the 500-record limit. Please upload a file containing 500 records or fewer.");
        return;
      }

      message.success(`File is valid with ${rows.length} records.`);
      handleFileChangeImport(file);
    };

    reader.readAsArrayBuffer(file);

    return false;
  };

  const handleChange = (info) => {
    const { status, percent } = info.file

    if (status === "uploading") {
      setUploading(true)
      setUploadComplete(false)
      setProgress(Math.floor(percent) || 30)
    }

    if (status === "done") {
      message.success(`${info.file.name} uploaded successfully.`)
      setUploading(false)
      setProgress(100)
      setUploadComplete(true)
    } else if (status === "error") {
      message.error(`${info.file.name} upload failed.`)
      setUploading(false)
      setProgress(0)
      setUploadComplete(false)
    }

    setFileList(info.fileList)
    onFileListChange?.(info.fileList)
  }

  const handleRemove = (file) => {
    const updatedList = fileList.filter((f) => f.uid !== file.uid)
    setFileList(updatedList)
    setProgress(0)
    setUploading(false)
    setUploadComplete(false)
    setFileData(null)
    return true
  }

  return (
    <div >
      <Dragger
        name="file"
        multiple={false}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onRemove={handleRemove}
        fileList={fileList}
        showUploadList={false}
        accept=".xlsx"
        customRequest={({ file, onSuccess }) => {
          setUploading(true)
          let percent = 0
          const interval = setInterval(() => {
            percent += 25
            setProgress(percent)
            if (percent >= 100) {
              clearInterval(interval)
              setUploading(false)
              setUploadComplete(true)
              onSuccess("ok")
            }
          }, 500)
        }}
      >
        {fileList.length === 0 ? (
          <div className="flex-row gap-xs">
            <div className={styles.circle} >
              <Icon name="Upload" color={colors.textPrimary} width={16} height={16} strokeWidth={1.4} />
            </div>
            <div className="align-center justify-center">
              <div className="flex-column align-center">
                <Text variant="link" className="mb-xxs">
                  {t("global.upload")}
                </Text>
                <Text className="flex-1" variant="caption" size="lg">
                  {t("clients.only_xlsx_format_supported_with_file_size_limit")}

                </Text>
              </div>
            </div>
          </div>
        ) : (
          <Flex className={styles.filePreview} justify="space-between" align="center" style={{ width: "100%" }}>
            <Col style={{ flex: 1 }}>
              <div className={styles.fileInfo}>
                <File style={{ fontSize: "18px", color: "#1677ff" }} />
                <span className="fileName">{fileList[0].name}</span>
              </div>

              {uploading && !uploadComplete && (
                <div className={styles.progressContainer}>
                  <Progress
                    percent={progress}
                    size="small"
                    status="active"
                    showInfo={false}
                    strokeColor="#1677ff"
                    trailColor="#f0f0f0"
                    style={{ width: "60%" }}
                  />
                  <span className={styles.progressText}>{progress}% done</span>
                </div>
              )}
            </Col>

            <Col className={styles.rightInfo}>
              <DeleteOutlined onClick={() => handleRemove(fileList[0])} className={styles.deleteIcon} />
            </Col>
          </Flex>
        )}
      </Dragger>
    </div>
  )
}

UploadFile.propTypes = {
  onFileListChange: PropTypes.func,
  handleFileChangeImport: PropTypes.func.isRequired,
  setFileData: PropTypes.func
}

export default UploadFile
